// Shared vocabulary for the Payments module.
//
// The UI says "Recurring" / "One-time" / "Variable" while the API stores
// 'subscription' / 'onetime' / 'usage' — see
// backend_reception/src/utils/billingCycles.js for why the stored value for a
// variable payment is not literally 'variable'. Everything user-facing goes
// through the labels below; the raw values only ever cross the wire.

export const PAYMENT_TYPES = {
    subscription: {
        value: 'subscription',
        label: 'Recurring',
        icon: '🔁',
        blurb: 'Renews on a schedule. We remind you before it charges.',
        examples: 'Claude · Hostinger · Shopify · Apple Developer'
    },
    onetime: {
        value: 'onetime',
        label: 'One-time',
        icon: '🧾',
        blurb: 'Paid once and finished. Never appears in upcoming renewals.',
        examples: 'Google Play Console · Chrome Web Store · Setup fee'
    },
    usage: {
        value: 'usage',
        label: 'Variable',
        icon: '⚡',
        blurb: 'No fixed date or amount. You record each charge as it happens.',
        examples: 'Google Ads · Meta Ads · OpenAI API credits'
    }
};

// Lifecycle of a subscription — distinct from the status of an individual
// payment. 'trial' and 'active' are both live.
export const SUBSCRIPTION_STATUSES = {
    trial: { label: 'Trial', tone: 'warning' },
    active: { label: 'Active', tone: 'success' },
    paused: { label: 'Paused', tone: 'muted' },
    cancelled: { label: 'Cancelled', tone: 'muted' },
    expired: { label: 'Completed', tone: 'muted' },
    deleted: { label: 'Deleted', tone: 'muted' }
};

// What an individual payment did. 'scheduled' only ever appears as a derived
// row — it is never stored, so it cannot be counted as money spent.
export const PAYMENT_STATUSES = {
    scheduled: { label: 'Scheduled', tone: 'info' },
    paid: { label: 'Paid', tone: 'success' },
    failed: { label: 'Failed', tone: 'danger' },
    refunded: { label: 'Refunded', tone: 'muted' }
};

export const BILLING_CYCLES = [
    { value: 'weekly', label: 'Weekly', shortLabel: 'week' },
    { value: 'monthly', label: 'Monthly', shortLabel: 'month' },
    { value: 'quarterly', label: '3 months', shortLabel: '3 months' },
    { value: 'half-yearly', label: '6 months', shortLabel: '6 months' },
    { value: 'yearly', label: 'Yearly', shortLabel: 'year' },
    { value: 'variable', label: 'Custom', shortLabel: 'custom' }
];

// A fixed list rather than free text: typed currencies ("QAR", "qar", "QR")
// would make every total wrong, the same way free-text categories break
// grouped reporting.
export const CURRENCIES = ['QAR', 'USD', 'EUR', 'GBP', 'SAR', 'AED', 'INR'];

// Most of these vendors bill in USD while the card is charged in QAR. QAR is
// pegged at 3.64 per USD — used ONLY to pre-fill a suggestion the person can
// overwrite with the statement figure, never to convert a stored amount.
export const USD_TO_QAR = 3.64;

// Suggestions, not a closed list — existing records hold values like
// "rishin credit card" and forcing them into an enum would lose them.
export const PAYMENT_METHODS = [
    'Credit Card', 'Debit Card', 'Bank Transfer', 'PayPal',
    'Apple Pay', 'Google Pay', 'Cash', 'Other'
];

export const REMINDER_OFFSETS = [30, 14, 7, 3, 1, 0];

export const offsetLabel = (days) => {
    if (days === 0) return 'On the day';
    if (days === 1) return '1 day';
    return `${days} days`;
};

// Card brands offered for a company card. Mirrors CARD_BRANDS in
// backend_reception/src/models/CompanyCard.js.
export const CARD_BRANDS = [
    { value: 'visa', label: 'Visa' },
    { value: 'mastercard', label: 'Mastercard' },
    { value: 'amex', label: 'Amex' },
    { value: 'other', label: 'Other' }
];

// Same wording as CompanyCard.labelFor on the backend — "Visa ••4321 · Rishin"
export const cardLabel = (card) => {
    if (!card) return '';
    if (card.label) return card.label;
    const brand = CARD_BRANDS.find(b => b.value === card.brand)?.label;
    const base = `${brand && brand !== 'Other' ? brand : 'Card'} ••${card.last4}`;
    return card.nickname ? `${base} · ${card.nickname}` : base;
};

// "11/28", plus whether it has run out or is about to (end of the printed month)
export const cardExpiry = (card) => {
    if (!card?.expiryMonth || !card?.expiryYear) return null;
    const now = new Date();
    const monthsLeft = (card.expiryYear * 12 + card.expiryMonth - 1) - (now.getFullYear() * 12 + now.getMonth());
    return {
        text: `${String(card.expiryMonth).padStart(2, '0')}/${String(card.expiryYear).slice(-2)}`,
        expired: monthsLeft < 0,
        soon: monthsLeft >= 0 && monthsLeft <= 2
    };
};

// "Ahlamart › Ahlamart - Main", or just the company for a company-wide cost
export const costCentreLabel = (record = {}) =>
    [record.companyName, record.branchName].filter(Boolean).join(' › ');

// The id the "Charged to" picker should show for a record — its branch, else
// its company (a company-wide cost).
export const costCentreId = (record = {}) =>
    String(record.branch?._id || record.branch || record.company?._id || record.company || '');

// Company/branch picker options from the flat Tenant list returned by
// /api/subscriptions/lookups — each company followed by its branches.
export const costCentreGroups = (tenants = []) => {
    const companies = tenants.filter(t => t.type === 'company');
    return companies.map(company => ({
        company,
        branches: tenants.filter(t => t.type === 'branch' && String(t.parent) === String(company._id))
    }));
};

// ---- formatting ----

export const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatDateShort = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Payments carry a real time — "when exactly did the card get charged" is what
// matches a bank SMS or statement line.
export const formatDateTime = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${day}, ${time}`;
};

export const formatDateLong = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const formatAmount = (amount, currency) =>
    `${currency || 'QAR'} ${Number(amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;

// Compact form for KPI tiles, where two decimals on a five-figure number is noise
export const formatAmountShort = (amount, currency) =>
    `${currency || 'QAR'} ${Math.round(Number(amount) || 0).toLocaleString('en-US')}`;

export const toDateInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
};

// <input type="datetime-local"> value in the browser's own time zone
export const toDateTimeInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Send an absolute instant. A bare "2026-10-12T14:30" would be read by the
// server in ITS zone — UTC — and land three hours off.
export const fromDateTimeInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString();
};

// Days until due: negative = overdue
export const daysUntil = (date) => {
    if (!date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(date);
    due.setHours(0, 0, 0, 0);
    return Math.round((due - today) / (1000 * 60 * 60 * 24));
};

// "Today" / "Tomorrow" / "In 5 days" / "3 days overdue" — the phrasing people
// actually use, rather than a raw date they have to subtract in their head.
export const relativeDay = (days) => {
    if (days == null) return '';
    if (days < -1) return `${Math.abs(days)} days overdue`;
    if (days === -1) return '1 day overdue';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 7) return `In ${days} days`;
    if (days < 14) return 'Next week';
    return `In ${days} days`;
};

// Amounts are only ever summed within a currency — there are no FX rates in
// this system, so QAR + USD would be a meaningless number.
export const splitCurrencyTotals = (totals = {}, compact = false) => {
    const format = compact ? formatAmountShort : formatAmount;
    const entries = Object.entries(totals).filter(([, value]) => value > 0);
    if (entries.length === 0) return { primary: format(0, 'QAR'), extra: [] };

    entries.sort((a, b) => {
        if (a[0] === 'QAR') return -1;
        if (b[0] === 'QAR') return 1;
        return b[1] - a[1];
    });

    return {
        primary: format(entries[0][1], entries[0][0]),
        extra: entries.slice(1).map(([currency, value]) => format(value, currency))
    };
};

// Mirror of advanceDueDate in backend_reception/src/utils/billingCycles.js,
// used only to preview upcoming dates while the form is being filled in. The
// backend remains the source of truth for what actually gets stored.
export const advanceDueDate = (dueDate, billingCycle, anchorDay, reviewFrequencyDays) => {
    const d = new Date(dueDate);
    if (isNaN(d.getTime())) return null;

    if (billingCycle === 'variable') {
        const next = new Date(d);
        next.setDate(next.getDate() + (Number(reviewFrequencyDays) || 30));
        return next;
    }
    if (billingCycle === 'weekly') {
        const next = new Date(d);
        next.setDate(next.getDate() + 7);
        return next;
    }

    const monthStep = { monthly: 1, quarterly: 3, 'half-yearly': 6, yearly: 12 }[billingCycle];
    if (!monthStep) return null;

    const day = anchorDay || d.getDate();
    const target = d.getMonth() + monthStep;
    const year = d.getFullYear() + Math.floor(target / 12);
    const month = ((target % 12) + 12) % 12;
    const daysInTargetMonth = new Date(year, month + 1, 0).getDate();

    const next = new Date(d);
    next.setFullYear(year, month, Math.min(day, daysInTargetMonth));
    return next;
};

export const isRecurring = (record = {}) => (record.paymentType || 'subscription') === 'subscription';

export const chargesPerMonth = (payment = {}) => {
    if (!isRecurring(payment)) return 0;
    const cycle = payment.billingCycle || 'monthly';
    if (cycle === 'variable') return (365.25 / 12) / (payment.reviewFrequencyDays || 30);
    return { weekly: 52 / 12, monthly: 1, quarterly: 1 / 3, 'half-yearly': 1 / 6, yearly: 1 / 12, none: 0 }[cycle] ?? 0;
};

export const monthlyCost = (payment = {}) => (Number(payment.amount) || 0) * chargesPerMonth(payment);

export const cycleLabel = (payment = {}) => {
    if (!isRecurring(payment)) return PAYMENT_TYPES[payment.paymentType]?.label || '';
    if (payment.billingCycle === 'variable') return `Every ${payment.reviewFrequencyDays || 30} days`;
    return BILLING_CYCLES.find(c => c.value === payment.billingCycle)?.label || 'Monthly';
};

// "per month" / "per year" phrasing used under the amount on a card
export const cyclePerLabel = (payment = {}) => {
    if (!isRecurring(payment)) return '';
    if (payment.billingCycle === 'variable') return `every ${payment.reviewFrequencyDays || 30} days`;
    const short = BILLING_CYCLES.find(c => c.value === payment.billingCycle)?.shortLabel;
    return short ? `per ${short}` : 'per month';
};
